/**
 * @fileoverview Middleware Redux para sincronização automática dos favoritos.
 * Intercepta ações dos favoritos e sincroniza com servidor quando usuário autenticado.
 * 
 * @module favoritesSyncMiddleware
 * @since 1.0.0
 */

import { saveFavoritesAsync, processFavoriteActionAsync } from '../slices/favoritesSlice';

/**
 * Middleware para sincronização automática dos favoritos.
 * Intercepta ações de modificação dos favoritos e dispara sincronização atômica.
 * 
 * @param {Object} store - Store Redux
 * @returns {Function} Middleware function
 */
export const favoritesSyncMiddleware = (store) => (next) => (action) => {
  const prevState = store.getState();
  const result = next(action);
  const currentState = store.getState();
  
  // Ações que devem disparar sincronização atômica
  if (action.type === 'favorites/toggleFavorite') {
    const { isAuthenticated, user, isLoggingOut } = currentState.auth;
    const { needsSync } = currentState.favorites;
    
    // Não sincronizar durante logout ou se não autenticado
    if (isAuthenticated && user?.email && needsSync && !isLoggingOut) {
      const product = action.payload;
      const favoriteProduct = {
        productId: String(product.id),
        name: String(product.nome || ''),
        brand: String(product.marca || ''),
        image: String(product.imagem || ''),
        category: String(product.categoria || '')
      };
      
      // Determinar ação baseada no estado anterior
      const wasInFavorites = prevState.favorites.items.some(fav => fav.productId === product.id);
      const actionType = wasInFavorites ? 'delete' : 'insert';
      
      // Debounce para evitar muitas requisições
      clearTimeout(window.favoritesSyncTimeout);
      window.favoritesSyncTimeout = setTimeout(() => {
        // Verificar novamente se ainda está autenticado
        const latestState = store.getState();
        if (!latestState.auth.isAuthenticated) return;
        
        store.dispatch(processFavoriteActionAsync({
          action: actionType,
          favoritesData: [favoriteProduct]
        }));
      }, 500);
    }
  }
  
  // Ação de limpar favoritos (método legado)
  if (action.type === 'favorites/clearFavoritesWithSync') {
    const { isAuthenticated, user, isLoggingOut } = currentState.auth;
    const { needsSync } = currentState.favorites;
    
    if (isAuthenticated && user?.email && needsSync && !isLoggingOut) {
      clearTimeout(window.favoritesSyncTimeout);
      window.favoritesSyncTimeout = setTimeout(() => {
        const latestState = store.getState();
        if (!latestState.auth.isAuthenticated) return;
        
        store.dispatch(saveFavoritesAsync([]));
      }, 500);
    }
  }
  
  return result;
};

export default favoritesSyncMiddleware;